// Soạn thông báo (chỉ Super): viết chữ và/hoặc đính ảnh, chọn người nhận (mọi Boss hoặc từng Boss), xem trước đúng như Boss sẽ thấy, gửi.
// Boss nhận bằng cửa sổ nổi giữa màn hình (dùng lại hộp thông báo). Bên dưới là lịch sử: đã gửi cho ai, bao nhiêu người đã đọc, thu hồi.
// Ảnh được thu nhỏ ngay trên trình duyệt (cạnh dài ≤ 1600 px) trước khi gửi để nhẹ; GIF giữ nguyên (mất hoạt hình nếu thu nhỏ).
import { api, buildQuery, errMsg } from "../api";
import { busy, clear, confirmBox, gen, h, toast } from "../dom";
import { fmtTime } from "../format";
import { renderNoticeCard } from "../notices";
import { isSuper } from "../store";
import type { UserWithBots } from "../types";
import { noPermission } from "./users";

const MAX_TITLE = 120, MAX_TEXT = 5000, MAX_IMAGES = 4, MAX_IMAGE_BYTES = 1_500_000, EDGE = 1600;
const OK_TYPES = ["image/png", "image/jpeg", "image/gif", "image/webp"];

interface Img { name: string; mime: string; data: string; src: string }
// tatCa (V1): gửi "Tất cả Boss" = phạm vi — Boss tạo SAU lúc gửi cũng nhận; recipients = số Boss đang hoạt động HIỆN NAY.
interface Hist { id: string; at: string; level: "info" | "warn"; title: string; text: string; images: number; recipients: number; read: number; tatCa?: boolean; from: string }
interface HistPage { items: Hist[]; nextBefore: string | null }
interface Full { id: string; at: string; level: "info" | "warn" | "err"; recipients: number; data: { kind: string; title?: string; text?: string; images?: { id: string }[]; from?: { username?: string } } | null }

function fileToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] ?? "");
    r.onerror = () => reject(new Error("Không đọc được tệp ảnh"));
    r.readAsDataURL(blob);
  });
}

/** Chuẩn bị một ảnh để gửi: kiểm loại, thu nhỏ (trừ GIF) tới khi ≤ 1,5 MB. Ném lỗi tiếng Việt nếu không dùng được. */
async function prepareImage(file: File): Promise<Img> {
  if (!OK_TYPES.includes(file.type)) throw new Error(`“${file.name}” không phải ảnh PNG, JPEG, GIF hoặc WEBP`);
  if (file.type === "image/gif") {
    if (file.size > MAX_IMAGE_BYTES) throw new Error(`“${file.name}” là ảnh GIF quá lớn (tối đa 1,5 MB)`);
    return { name: file.name, mime: file.type, data: await fileToBase64(file), src: URL.createObjectURL(file) };
  }
  const bmp = await createImageBitmap(file).catch(() => { throw new Error(`“${file.name}” không mở được như một ảnh`); });
  let scale = Math.min(1, EDGE / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement("canvas");
  for (const q of [0.88, 0.75, 0.6, 0.45]) {
    canvas.width = Math.max(1, Math.round(bmp.width * scale));
    canvas.height = Math.max(1, Math.round(bmp.height * scale));
    const cx = canvas.getContext("2d")!;
    cx.fillStyle = "#fff"; // JPEG không có nền trong suốt
    cx.fillRect(0, 0, canvas.width, canvas.height);
    cx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((ok) => canvas.toBlob(ok, "image/jpeg", q));
    if (blob && blob.size <= MAX_IMAGE_BYTES) return { name: file.name, mime: "image/jpeg", data: await fileToBase64(blob), src: URL.createObjectURL(blob) };
    scale *= 0.8;
  }
  throw new Error(`“${file.name}” vẫn quá lớn sau khi thu nhỏ (tối đa 1,5 MB)`);
}

export async function renderAnnounce(root: HTMLElement): Promise<() => void> {
  if (!isSuper()) return noPermission(root, "Soạn thông báo");
  const alive = gen("announce");
  const images: Img[] = [];
  let bosses: { id: string; username: string }[] = [];

  const title = h("input", { maxlength: MAX_TITLE, placeholder: "Tiêu đề (không bắt buộc)", title: `Dòng chữ đậm đầu thông báo, tối đa ${MAX_TITLE} ký tự`, "data-testid": "an-title" });
  const text = h("textarea", { rows: 6, maxlength: MAX_TEXT, placeholder: "Nội dung thông báo… (xuống dòng được giữ nguyên; địa chỉ web tự thành liên kết bấm được)", title: `Nội dung chữ thuần, tối đa ${MAX_TEXT} ký tự — không nhận định dạng HTML`, "data-testid": "an-text" });
  const count = h("span", { class: "small muted", "data-testid": "an-count" }, `0/${MAX_TEXT}`);
  text.addEventListener("input", () => { count.textContent = `${text.value.length}/${MAX_TEXT}`; });
  const level = h("input", { type: "checkbox", title: "Đánh dấu quan trọng: cửa sổ thông báo viền màu cam để Boss chú ý hơn", "data-testid": "an-level" });
  const file = h("input", { type: "file", accept: OK_TYPES.join(","), multiple: "multiple", hidden: true, title: "Chọn ảnh PNG, JPEG, GIF hoặc WEBP để đính kèm", "data-testid": "an-file" });
  const pick = h("button", { type: "button", title: `Đính kèm ảnh (tối đa ${MAX_IMAGES} ảnh, mỗi ảnh tự thu nhỏ còn nhẹ trước khi gửi)`, "data-testid": "an-add-image", onclick: () => file.click() }, "Thêm ảnh");
  const thumbs = h("div", { class: "an-thumbs", "data-testid": "an-thumbs" });
  const err = h("p", { class: "err small", hidden: true, "data-testid": "an-error" });
  const toAll = h("input", { type: "radio", name: "an-to", checked: true, title: "Gửi cho mọi Boss đang hoạt động — Boss tạo sau này cũng sẽ thấy", "data-testid": "an-to-all" });
  const toPick = h("input", { type: "radio", name: "an-to", title: "Chỉ gửi cho những Boss được tick bên dưới", "data-testid": "an-to-pick" });
  const bossBox = h("div", { class: "an-boss", "data-testid": "an-boss-list", hidden: true });
  const preview = h("button", { type: "button", title: "Xem trước đúng như Boss sẽ thấy (chưa gửi)", "data-testid": "an-preview" }, "Xem trước");
  const send = h("button", { type: "button", class: "pri", title: "Gửi thông báo cho những người đã chọn — Boss đang mở web thấy ngay, người khác thấy khi đăng nhập", "data-testid": "an-send" }, "Gửi");
  const histBody = h("tbody", { "data-testid": "an-rows" });
  const histEmpty = h("p", { class: "empty", hidden: true, "data-testid": "an-empty" }, "Chưa gửi thông báo nào.");
  const more = h("button", { type: "button", hidden: true, title: "Tải thêm các thông báo cũ hơn", "data-testid": "an-more" }, "Xem thêm");
  let nextBefore: string | null = null;

  const chonBoss = () => [...bossBox.querySelectorAll<HTMLInputElement>("input:checked")].map((i) => i.value);
  function setPick(on: boolean): void { bossBox.hidden = !on; }
  toAll.addEventListener("change", () => setPick(false));
  toPick.addEventListener("change", () => setPick(true));

  function paintThumbs(): void {
    clear(thumbs);
    images.forEach((im, i) => thumbs.append(h("div", { class: "an-thumb", "data-testid": "an-thumb" },
      h("img", { src: im.src, alt: `Ảnh đính kèm ${i + 1}` }),
      h("button", { type: "button", class: "an-thumb-x", title: `Bỏ ảnh ${i + 1} khỏi thông báo`, "aria-label": `Bỏ ảnh ${i + 1}`, "data-testid": "an-thumb-x", onclick: () => { images.splice(i, 1); paintThumbs(); } }, "×"))));
    pick.disabled = images.length >= MAX_IMAGES;
  }
  file.addEventListener("change", () => void (async () => {
    err.hidden = true;
    const files = [...(file.files ?? [])];
    file.value = "";
    for (const f of files) {
      if (images.length >= MAX_IMAGES) { err.textContent = `Tối đa ${MAX_IMAGES} ảnh mỗi thông báo`; err.hidden = false; break; }
      try { images.push(await prepareImage(f)); } catch (e) { err.textContent = errMsg(e); err.hidden = false; }
    }
    paintThumbs();
  })());

  function draft(): { title: string; text: string; level: "info" | "warn"; images: { mime: string; data: string }[] } | null {
    const t = title.value.trim(), x = text.value.trim();
    if (!t && !x && images.length === 0) { err.textContent = "Cần ít nhất tiêu đề, nội dung hoặc một ảnh"; err.hidden = false; return null; }
    err.hidden = true;
    return { title: t, text: x, level: level.checked ? "warn" : "info", images: images.map((i) => ({ mime: i.mime, data: i.data })) };
  }

  /** Cửa sổ xem trước: dùng ĐÚNG hàm vẽ thẻ của cửa sổ nổi Boss nên nhìn giống hệt. */
  function showCard(cardData: Full["data"], lvl: "info" | "warn" | "err", at: string, imageSrc: (i: number) => string | undefined, testid: string): void {
    const d = cardData ? { ...cardData, images: (cardData.images ?? []).map((im, i) => ({ ...im, src: imageSrc(i) })) } : null;
    const box = h("div", { class: "modal-wrap", role: "dialog", "aria-modal": "true", "aria-label": "Xem trước thông báo", "data-testid": testid },
      h("div", { class: "modal notice-popup" },
        h("button", { type: "button", class: "np-x", "aria-label": "Đóng xem trước", title: "Đóng cửa sổ xem trước", "data-testid": `${testid}-x`, onclick: () => box.remove() }, "×"),
        h("h3", {}, "Boss sẽ thấy như thế này"),
        h("div", { class: "np-body" }, renderNoticeCard({ id: "xem-truoc", at, level: lvl, text: "", seen: false, data: d })),
        h("div", { class: "row end" }, h("button", { type: "button", class: "pri", title: "Đóng cửa sổ xem trước", "data-testid": `${testid}-close`, onclick: () => box.remove() }, "Đóng"))));
    document.body.append(box);
  }
  preview.addEventListener("click", () => {
    const d = draft();
    if (!d) return;
    showCard({ kind: "thong_bao", title: d.title, text: d.text, images: images.map((_, i) => ({ id: String(i) })), from: { username: "bạn" } }, d.level, new Date().toISOString(), (i) => images[i]?.src, "an-preview-box");
  });

  send.addEventListener("click", () => void busy(send, "Đang gửi…", async () => {
    const d = draft();
    if (!d) return;
    const to = toAll.checked ? "all" : chonBoss();
    if (Array.isArray(to) && !to.length) { err.textContent = "Chưa chọn người nhận: hãy tick ít nhất một Boss"; err.hidden = false; return; }
    const n = toAll.checked ? bosses.length : (to as string[]).length;
    const ok = await confirmBox({ title: "Gửi thông báo?", text: `Sẽ gửi cho ${n} Boss${d.images.length ? `, kèm ${d.images.length} ảnh` : ""}. Boss đang mở web thấy ngay dạng cửa sổ giữa màn hình; ai chưa mở sẽ thấy khi đăng nhập.${toAll.checked ? " Đã chọn “Tất cả Boss”: Boss tạo sau này cũng sẽ thấy thông báo này." : ""}`, okText: "Gửi", okTitle: "Gửi thông báo ngay" });
    if (!ok) return;
    try {
      const r = await api<{ id: string; recipients: number; online: number }>("POST", "/api/announcements", { ...d, to });
      toast(`Đã gửi cho ${r.recipients} Boss (${r.online} người đang mở web thấy ngay)${to === "all" ? "; Boss tạo sau cũng sẽ thấy" : ""}`);
      title.value = ""; text.value = ""; count.textContent = `0/${MAX_TEXT}`; level.checked = false; images.length = 0; paintThumbs();
      await loadHistory(true);
    } catch (e) { err.textContent = errMsg(e); err.hidden = false; }
  }));

  function row(r: Hist): HTMLElement {
    const xem = h("button", { type: "button", class: "mini", title: "Xem lại đúng như Boss đã thấy", "data-testid": "an-view", onclick: (e: Event) => void busy(e.currentTarget as HTMLButtonElement, "…", async () => {
      try {
        const f = await api<Full>("GET", `/api/announcements/${encodeURIComponent(r.id)}`);
        showCard(f.data, f.level, f.at, (i) => (f.data?.images?.[i] ? `/api/announce/images/${f.data.images[i]!.id}` : undefined), "an-view-box");
      } catch (er) { toast(errMsg(er), "err"); }
    }) }, "Xem");
    const thuHoi = h("button", { type: "button", class: "mini danger", title: "Thu hồi: xoá thông báo này khỏi hộp thông báo của mọi Boss (ai chưa đọc sẽ không còn thấy)", "data-testid": "an-recall", onclick: () => void (async () => {
      const ok = await confirmBox({ title: "Thu hồi thông báo?", text: `“${r.title || r.text || "(chỉ có ảnh)"}” sẽ biến mất khỏi hộp thông báo của ${r.recipients} Boss, kể cả ảnh. Không hoàn tác được.`, okText: "Thu hồi", okTitle: "Thu hồi thông báo — không hoàn tác được", danger: true });
      if (!ok) return;
      try { await api("DELETE", `/api/announcements/${encodeURIComponent(r.id)}`); toast("Đã thu hồi thông báo"); await loadHistory(true); } catch (er) { toast(errMsg(er), "err"); }
    })() }, "Thu hồi");
    return h("tr", { "data-testid": "an-row", dataset: { id: r.id } },
      h("td", { class: "nowrap small" }, fmtTime(r.at)),
      h("td", { class: "wrap" }, r.level === "warn" ? [h("span", { class: "tag warn", title: "Đánh dấu quan trọng" }, "quan trọng"), " "] : null, h("b", { "data-testid": "an-row-title" }, r.title || "(không tiêu đề)"),
        r.text ? h("div", { class: "small muted wrap", "data-testid": "an-row-text" }, r.text) : null, r.images ? h("div", { class: "small muted" }, `kèm ${r.images} ảnh`) : null),
      h("td", { class: "nowrap small", "data-testid": "an-row-to", title: r.tatCa ? "Gửi cho tất cả Boss: mọi Boss đang hoạt động, kể cả Boss tạo sau lúc gửi" : "Chỉ những Boss được chọn lúc gửi" }, r.tatCa ? "Tất cả Boss" : `${r.recipients} Boss đã chọn`),
      h("td", { class: "nowrap", "data-testid": "an-row-read", title: r.tatCa ? "Số Boss đã đọc / số Boss đang hoạt động hiện nay (Boss mới tạo cũng được tính)" : "Số người đã đọc / tổng số người nhận" }, `${r.read}/${r.recipients}`),
      h("td", { class: "nowrap" }, xem, " ", thuHoi));
  }
  async function loadHistory(reset: boolean): Promise<void> {
    const g = gen("announce-history");
    try {
      const p = await api<HistPage>("GET", `/api/announcements${buildQuery({ limit: 50, before: reset ? undefined : nextBefore })}`);
      if (!g() || !alive()) return;
      if (reset) clear(histBody);
      for (const r of p.items) histBody.append(row(r));
      nextBefore = p.nextBefore;
      more.hidden = !nextBefore;
      histEmpty.hidden = histBody.children.length > 0;
    } catch (e) { if (g() && alive()) toast(`Không tải được lịch sử: ${errMsg(e)}`, "err"); }
  }
  more.addEventListener("click", () => void busy(more, "Đang tải…", () => loadHistory(false)));

  root.append(h("h1", {}, "Soạn thông báo"),
    h("p", { class: "small muted" }, "Viết chữ và/hoặc đính ảnh rồi gửi cho các Boss. Boss đang mở web thấy ngay thành cửa sổ giữa màn hình (có dấu × để đóng); ai chưa mở sẽ thấy khi đăng nhập và luôn xem lại được ở nút chuông."),
    h("div", { class: "card" },
      h("label", { class: "f" }, h("span", {}, "Tiêu đề"), title),
      h("label", { class: "f" }, h("span", {}, "Nội dung ", count), text),
      h("div", { class: "row" }, pick, file, h("label", { class: "inline", title: "Đánh dấu quan trọng" }, level, "Quan trọng")),
      thumbs,
      h("h3", {}, "Người nhận"),
      h("div", { class: "row" }, h("label", { class: "inline", title: "Mọi Boss đang hoạt động, kể cả Boss tạo sau này" }, toAll, "Tất cả Boss (cả Boss tạo sau)"), h("label", { class: "inline" }, toPick, "Chọn từng Boss")),
      bossBox, err,
      h("div", { class: "row" }, preview, send)),
    h("div", { class: "card" }, h("h3", {}, "Đã gửi"),
      h("div", { class: "tbl" }, h("table", { "data-testid": "an-history" }, h("thead", {}, h("tr", {}, h("th", {}, "Lúc"), h("th", {}, "Nội dung"), h("th", { title: "Tất cả Boss (kể cả Boss tạo sau) hoặc những Boss được chọn" }, "Gửi tới"), h("th", { title: "Số người đã đọc / tổng số người nhận" }, "Đã đọc"), h("th", {}, ""))), histBody)),
      histEmpty, h("div", { class: "row center" }, more)));

  try {
    const us = await api<UserWithBots[]>("GET", "/api/users");
    if (!alive()) return () => {};
    bosses = us.filter((u) => u.role === "boss" && !u.disabled).map((u) => ({ id: u.id, username: u.username }));
    for (const b of bosses) bossBox.append(h("label", { class: "inline" }, h("input", { type: "checkbox", value: b.id, title: `Gửi cho ${b.username}`, "data-testid": "an-boss" }), b.username));
    toAll.title = `Gửi cho mọi Boss đang hoạt động (hiện có ${bosses.length} người) — Boss tạo sau này cũng sẽ thấy`;
  } catch (e) { toast(`Không tải được danh sách Boss: ${errMsg(e)}`, "err"); }
  await loadHistory(true);
  return () => { for (const im of images) URL.revokeObjectURL(im.src); };
}
